import { useState } from 'react';
import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Input, Form, Row, Col, Button } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import type { EntitlementOut } from '@/api/generated/schemas';
import { EntitlementStatus } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useAdminTable } from '@/hooks/useAdminTable';
import { TextFilter } from '@/components/common/TextFilter';
import { SelectFilter } from '@/components/common/SelectFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { EntitlementStatusTag } from '@/components/monetization/EntitlementStatusTag';
import { GrantEntitlementModal } from '@/components/monetization/GrantEntitlementModal';
import { ShowLink } from '@/components/common/ShowLink';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const EntitlementList = () => {
  const t = useTranslate();
  const { onSearch, createDebouncedSubmit } = useTableSearch();
  const [grantOpen, setGrantOpen] = useState(false);

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<EntitlementOut>({
      pagination: { pageSize: 20 },
      resource: 'monetization.entitlements',
      onSearch,
      sorters: {
        initial: [{ field: 'id', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  return (
    <>
      <List
        headerButtons={() => (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setGrantOpen(true)}
          >
            {t('monetization.entitlements.grant.trigger')}
          </Button>
        )}
      >
        <Form {...searchFormProps} layout="vertical">
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={16} md={12} lg={8}>
              <Form.Item name="q" style={{ marginBottom: 0 }}>
                <Input
                  placeholder={t(
                    'monetization.entitlements.search.placeholder'
                  )}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => debouncedSubmit(e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Table
          {...tableProps}
          pagination={{ ...tableProps.pagination, simple: true }}
          rowKey="id"
        >
          <Table.Column
            dataIndex="id"
            title={t('base.fields.id')}
            sorter
            sortOrder={getSortOrder(sorters, 'id')}
            render={(value: number) => (
              <ShowLink resource="monetization.entitlements" id={value} />
            )}
          />

          <Table.Column
            dataIndex="account_id"
            title={t('monetization.entitlements.fields.account_id')}
            filtered={isFieldFiltered(filters as CrudFilter[], 'account_id')}
            filterDropdown={({ confirm, clearFilters }) => (
              <TextFilter
                field="account_id"
                width={160}
                placeholder={t('monetization.entitlements.filter.account_id')}
                filters={filters as CrudFilter[]}
                confirm={confirm}
                clearFilters={clearFilters}
                setFilters={setFilters}
              />
            )}
          />

          <Table.Column
            dataIndex="status"
            title={t('monetization.entitlements.fields.status')}
            sorter
            sortOrder={getSortOrder(sorters, 'status')}
            render={(value: EntitlementStatus) => (
              <EntitlementStatusTag value={value} />
            )}
            filtered={isFieldFiltered(filters as CrudFilter[], 'status')}
            filterDropdown={({ confirm, clearFilters }) => (
              <SelectFilter
                field="status"
                minWidth={160}
                placeholder={t('monetization.entitlements.filter.status')}
                options={Object.values(EntitlementStatus).map((value) => ({
                  label: t(`monetization.entitlements.status.${value}`),
                  value,
                }))}
                filters={filters as CrudFilter[]}
                confirm={confirm}
                clearFilters={clearFilters}
                setFilters={setFilters}
              />
            )}
          />

          <Table.Column
            dataIndex="valid_from"
            title={t('monetization.entitlements.fields.valid_from')}
            sorter
            sortOrder={getSortOrder(sorters, 'valid_from')}
            filtered={isFieldFiltered(filters as CrudFilter[], 'valid_from')}
            filterDropdown={({ confirm, clearFilters }) => (
              <DateRangeFilter
                field="valid_from"
                filters={filters as CrudFilter[]}
                confirm={confirm}
                clearFilters={clearFilters}
                setFilters={setFilters}
              />
            )}
            render={(value: string) =>
              value ? new Date(value).toLocaleString() : '-'
            }
          />

          <Table.Column
            dataIndex="valid_until"
            title={t('monetization.entitlements.fields.valid_until')}
            sorter
            sortOrder={getSortOrder(sorters, 'valid_until')}
            filtered={isFieldFiltered(filters as CrudFilter[], 'valid_until')}
            filterDropdown={({ confirm, clearFilters }) => (
              <DateRangeFilter
                field="valid_until"
                filters={filters as CrudFilter[]}
                confirm={confirm}
                clearFilters={clearFilters}
                setFilters={setFilters}
              />
            )}
            render={(value: string) =>
              value ? new Date(value).toLocaleString() : '-'
            }
          />

          <Table.Column
            dataIndex="limit_seconds"
            title={t('monetization.entitlements.fields.limit_seconds')}
            render={(value: number | null) => value ?? '-'}
          />

          <Table.Column
            dataIndex="consumed_seconds"
            title={t('monetization.entitlements.fields.consumed_seconds')}
          />

          <Table.Column
            dataIndex="limit_reached_at"
            title={t('monetization.entitlements.fields.limit_reached_at')}
            render={(value: string | null) =>
              value ? new Date(value).toLocaleString() : '-'
            }
          />
        </Table>
      </List>
      <GrantEntitlementModal
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
      />
    </>
  );
};
